import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Eye, EyeOff } from "lucide-react";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import { loginUser } from "../../store/slices/authSlice";
import { isEmail } from "../../utils";
import LoginCardBg from "../../assets/login-card-bg.svg";
import LoginCardBgBoth from "../../assets/login-card-bg-both.svg";
import LoginCardBgLoycom from "../../assets/login-card-bg-loycom.svg";
import LoginCardBgHandywelt from "../../assets/login-card-bg-handywelt.svg";
import LogoHandywelt from "../../assets/logo-handywelt.png";
import THEME from "../../utils/themeConfig";
import { getBaseRouting, getUserRole } from "../../config/roleFeatures";

const Login = () => {
  const isLoycomDomain =
    window.location.hostname === process.env.REACT_APP_WEB_LOYCOM_URL;
  const isHandyweltDomain =
    window.location.hostname === process.env.REACT_APP_WEB_HANDYWELT_URL;
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const { isLoading, isAuthenticated, user } = useAppSelector((state) => state.auth);
  const { isMobileURL, isAccessoriesURL, isPartsURL, isGodownURL, isHandyweltURL, isMobileLikeURL } = useAppSelector(
    (state) => state.domain
  );
  const usesMobileTheme = isMobileLikeURL || isHandyweltDomain;
  const isHandywelt = isHandyweltDomain || isHandyweltURL;

  const loginCardBg = isLoycomDomain
    ? LoginCardBgLoycom
    : isHandywelt
    ? LoginCardBgHandywelt
    : isGodownURL || (isMobileLikeURL && !isHandywelt)
    ? LoginCardBg
    : LoginCardBgBoth;
  
  const [formData, setFormData] = useState({
    email: "",
    password: "",
  });

  const [errors, setErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [serverError, setServerError] = useState("");

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      const userRole = getUserRole(user);
      navigate(getBaseRouting(userRole));
    }
  }, [isAuthenticated, user, navigate]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }

    // Clear server error when user starts typing
    if (serverError) {
      setServerError("");
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Email validation
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!isEmail(formData.email)) {
      newErrors.email =
        "Invalid email address. Please enter valid email address";
    }

    // Password validation - only check if password is provided for login
    if (!formData.password) {
      newErrors.password = "Password is required";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Clear any previous errors
    setErrors({});
    setServerError("");

    if (!validateForm()) {
      return;
    }

    try {
      const result = await dispatch(loginUser(formData));

      if (loginUser.fulfilled.match(result)) {
        // Login successful - user data is already set in Redux store
        const userRole = getUserRole(result.payload?.user);
        navigate(getBaseRouting(userRole));
      } else if (loginUser.rejected.match(result)) {
        // Handle server error
        const errorMessage =
          result.payload || "Login failed. Please try again.";
        const normalizedMessage = errorMessage.toLowerCase();

        // Check if it's an email-specific error
        if (
          normalizedMessage.includes("no account found with this email") ||
          (normalizedMessage.includes("email") &&
            normalizedMessage.includes("not found"))
        ) {
          setErrors({ email: errorMessage });
        }
        // Check if it's a password-specific error
        else if (
          normalizedMessage.includes("invalid password") ||
          normalizedMessage.includes("incorrect password") ||
          normalizedMessage.includes("wrong password")
        ) {
          setErrors({ password: errorMessage });
        } else {
          setServerError(errorMessage);
        }
      }
    } catch (error) {
      console.error("Login error:", error);
      setServerError("An unexpected error occurred. Please try again.");
    }
  };

  return (
    <div className="d-flex min-vh-100 align-items-center justify-content-center p-4 login-page-container">
      <div className={`login-card-container${isHandywelt ? " login-card-container--handywelt" : ""}`}>
        <div className="login-card-bg">
          <img src={loginCardBg} alt="" aria-hidden="true" />
        </div>

        {isHandywelt && (
          <div className="login-brand-logo-header">
            <img src={LogoHandywelt} alt="Handywelt" />
          </div>
        )}

        <div className={`login-content${isHandywelt ? " login-content--handywelt" : ""}`}>
          {/* Login Form */}
          <div className="mb-4">
            <h2 className="mb-2 mrph-txt size-20 weight-600 color-text-dark">
              Login
            </h2>
            <p className="mb-0 mrph-txt size-14 color-text-muted">
              Enter your credentials to continue your account
            </p>
          </div>

          <form onSubmit={handleSubmit} noValidate>
            {/* Email Field */}
            <div className="mb-4">
              <label
                htmlFor="email"
                className="mb-2 mrph-txt size-13 color-text-dark weight-600"
              >
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                className={`form-control mrph-form input-form ${
                  errors.email ? "is-invalid" : ""
                }`}
                placeholder="Enter your email"
              />
              {errors.email && (
                <div className="invalid-feedback d-block mt-1 error-message">
                  {errors.email}
                </div>
              )}
            </div>

            {/* Password Field */}
            <div className="mb-4">
              <label
                htmlFor="password"
                className="mb-2 mrph-txt size-13 color-text-dark weight-600"
              >
                Password
              </label>
              <div className="position-relative">
                <input
                  type={showPassword ? "text" : "password"}
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className={`form-control mrph-form input-form ${
                    errors.password ? "is-invalid" : ""
                  }`}
                  placeholder="Enter your password"
                />
                <button
                  type="button"
                  className="position-absolute end-0 top-50 translate-middle-y me-2 password-toggle-btn"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
              {errors.password && (
                <div className="invalid-feedback d-block mt-1 error-message">
                  {errors.password}
                </div>
              )}
            </div>

            {/* Server Error Display */}
            {serverError && (
              <div className="mb-3">
                <div className="invalid-feedback d-block error-message">
                  {serverError}
                </div>
              </div>
            )}

            {/* Login Button */}
            <button
              type="submit"
              className="w-100 mrph-btn login-btn"
              style={{
                backgroundColor: usesMobileTheme ? THEME["--primary-color-mobile"] : (isAccessoriesURL) ? THEME["--primary-color-acc"] : (isGodownURL) ? THEME["--primary-color-godown"] : isPartsURL ? THEME["--primary-color-parts"] : "",
                "--hover-color": usesMobileTheme ? THEME["--primary-hover-mobile"] : (isAccessoriesURL) ? THEME["--primary-hover-acc"] : (isGodownURL) ? THEME["--primary-hover-godown"] : isPartsURL ? THEME["--primary-hover-parts"] : "",
              }}
              disabled={isLoading}
            >
              {isLoading ? "Signing in..." : "Login"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Login;
